import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../auth/AuthContext';
import { format } from 'date-fns';

// Define typescript type for window object
declare global {
  interface Window {
    reloadNotifications?: () => void;
  }
}

// Define notification types
export type NotificationType = 'booking_confirmed' | 'booking_cancelled' | 'system';

// Define notification structure
export interface Notification {
  id: string;
  title: string;
  message: string;
  notification_type: NotificationType;
  read: boolean;
  created_at: Date;
  data?: any;
}

// Context interface
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

// Create the context
const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  fetchNotifications: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
});

// Custom hook to use notifications
export const useNotifications = () => useContext(NotificationContext);

// Provider component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useAuth();
  
  // Track processed notification IDs to prevent duplicates from multiple subscriptions
  const processedNotificationIds = React.useRef<Set<string>>(new Set());
  
  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Fetch notifications from database
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }
      
      if (data) {
        const mappedNotifications = data.map(notification => ({
          ...notification,
          id: notification.id,
          title: notification.title || '',
          message: notification.message || '',
          notification_type: notification.notification_type as NotificationType || 'system',
          read: notification.read || false,
          created_at: new Date(notification.created_at),
          data: notification.data
        }));
        
        setNotifications(mappedNotifications);
        
        // Populate processedNotificationIds with existing IDs
        mappedNotifications.forEach(n => {
          processedNotificationIds.current.add(n.id);
        });
      }
    } catch (err) {
      console.error('Error in fetchNotifications:', err);
    }
  }, [user]);
  
  // Add a global method to force a refresh of notifications
  useEffect(() => {
    if (user) {
      window.reloadNotifications = () => {
        fetchNotifications().catch(err => {
          console.error('Error during manual fetchNotifications:', err);
        });
      };
      
      return () => {
        delete window.reloadNotifications;
      };
    }
  }, [user, fetchNotifications]);
  
  // Fetch notifications on initial load and when user changes
  useEffect(() => {
    if (user) {
      fetchNotifications().catch(err => {
        console.error('Error during fetchNotifications in useEffect:', err);
      });
    } else {
      setNotifications([]);
    }
  }, [user, fetchNotifications]);
  
  // Setup Supabase realtime subscription for notifications
  useEffect(() => {
    if (!user) return;
    
    // Create a unique channel name to prevent duplicates
    const channelName = `notifications-${user.id}-${Date.now()}`;
    
    // Subscribe to the notifications table
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`
        },
        (payload) => {
          const notificationId = payload.new.id;
          
          // Check if we've already processed this notification (prevents duplicates from multiple subscriptions)
          if (processedNotificationIds.current.has(notificationId)) {
            return;
          }
          
          // Mark as processed immediately
          processedNotificationIds.current.add(notificationId);
          
          // Show browser notification if permission granted
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(payload.new.title, {
              body: payload.new.message
            });
          }
          
          // Add new notification to the state
          const newNotification: Notification = {
            ...payload.new,
            created_at: new Date(payload.new.created_at),
            id: payload.new.id,
            title: payload.new.title || '',
            message: payload.new.message || '',
            notification_type: payload.new.notification_type as NotificationType || 'system',
            read: payload.new.read || false,
            data: payload.new.data
          };
          
          setNotifications(prev => [newNotification, ...prev]);
        }
      )
      .subscribe();
    
    // Clean up subscription when component unmounts
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);
  
  // Mark a notification as read
  const markAsRead = async (id: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', id)
        .eq('recipient_id', user.id);
        
      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, read: true } 
            : notification
        )
      );
    } catch (err) {
      console.error('Error in markAsRead:', err);
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user || notifications.length === 0) return;
    
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('recipient_id', user.id)
        .eq('read', false);
        
      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (err) {
      console.error('Error in markAllAsRead:', err);
    }
  };
  
  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;